import { getCustomColumns, getStandardColumns } from '@townsquare/columns/hooks/use-shown-columns';
import { resolveRawSort } from '@townsquare/directory-query-params/sort';
import { createRelayResource, type RelayResourceConfig } from '@townsquare/relay-utils';
import type {
	ResourceStoreContext,
	RouterContext,
	RouterDataContext,
} from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';

import ProjectDirectoryQuery, {
	type DirectoryViewProjectQuery as ProjectDirectoryViewQueryType,
	type ProjectSortEnum,
} from '../components/__generated__/DirectoryViewProjectQuery.graphql';
import { DEFAULT_TABLE_PROJECT_SORTS, DEFAULT_TIMELINE_PROJECT_SORTS } from '../default-constants';
import {
	getDefaultProjectTableTqlQuery,
	getDefaultProjectTimelineTqlQuery,
	getStandardColumnRelayVariables,
} from '../utils';

export const DEFAULT_FIRST = 50;

export const getProjectDirectoryViewResourceQuery = (
	routerContext: RouterContext | RouterDataContext,
	resourceContext: ResourceStoreContext,
): RelayResourceConfig<ProjectDirectoryViewQueryType> => {
	const context = resourceContext as ResourceContext;
	const directoryViewUuid = routerContext.query.viewUuid;

	const isSavedView = !!directoryViewUuid;
	const isTable = Boolean(routerContext.query.view !== 'timeline');
	const isTableOrSavedView = isTable || isSavedView;
	const isTimelineOrSavedView = !isTable || isSavedView;
	const defaultTql = isTable
		? getDefaultProjectTableTqlQuery()
		: getDefaultProjectTimelineTqlQuery();
	const defaultSort = isTable ? DEFAULT_TABLE_PROJECT_SORTS : DEFAULT_TIMELINE_PROJECT_SORTS;
	const shouldUseDefaults = !directoryViewUuid;

	const selectedStandardColumns = getStandardColumns('projects', routerContext.query.columns);
	const selectedCustomColumns = getCustomColumns(routerContext.query.columns);

	return {
		query: ProjectDirectoryQuery,
		variables: {
			tql: routerContext.query.tql || (shouldUseDefaults ? defaultTql : null),
			first: DEFAULT_FIRST,
			sort:
				resolveRawSort<ProjectSortEnum>(routerContext.query.sort) ??
				(shouldUseDefaults ? defaultSort : null),
			workspaceUuid: context.workspace?.UUID || '',
			directoryViewUuid,
			isTableOrSavedView,
			isTimelineOrSavedView,
			...getStandardColumnRelayVariables(selectedStandardColumns),
			includedCustomFieldUuids: selectedCustomColumns,
			// We have to manually refetch once the columns have loaded in from the view.
			// See DirectoryTable.tsx
			skipTableTql: isSavedView && routerContext.query.columns === undefined,
		},
		options: {
			fetchPolicy: 'store-and-network',
		},
	};
};

export const projectDirectoryViewResource = createRelayResource<ProjectDirectoryViewQueryType>({
	resourceType: 'PROJECT_DIRECTORY_VIEW_RESOURCE',
	getQuery: getProjectDirectoryViewResourceQuery,
});
